import { Center } from "@freightsimple/ui-core/PageLayout/Center";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { SavedContact } from "@freightsimple/generated-dashboard-openapi-client/models/SavedContact";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { UUID } from "@/Shared/types";
import LocationsBox from "../CustomComponents/LocationsBox";
import { LocationsAddLocationButton } from "./LocationsAddLocationButton";
import { LocationsAddressBookTabs } from "./LocationsAddressBookTabs";

interface LocationsAddressBookTabContentProps {
  filter: string;
  showAddLocationDialog: () => void;
  filteredLocations: Array<SavedLocation>;
  showModifyLocationDialog: (savedLocation: SavedLocation) => void;
  showChangeContactDialog: (
    savedLocationId: UUID,
    defaultContact: SavedContact,
    contacts: Array<SavedContact>,
    defaultSavedContactId: UUID | undefined,
  ) => void;
  onToggleFavourite: (savedLocationId: UUID) => void;
  onToggleBranch: (savedLocationId: UUID) => void;
  onDelete: (savedLocationId: UUID, savedLocation: SavedLocation) => void;
  onToggleDefaultPickupLocation: (savedLocationId: UUID) => void;
  onToggleDefaultDeliveryLocation: (savedLocationId: UUID) => void;
  defaultPickupLocationId: UUID | undefined;
  defaultDeliveryLocationId: UUID | undefined;
  selectedTab: LocationsAddressBookTabs;
  onViewShipmentsToThisLocation: (savedLocation: SavedLocation) => void;
  onViewShipmentsFromThisLocation: (savedLocation: SavedLocation) => void;
  loadingLocations: Record<string, boolean>;
}

/**
 * The body of whichever address-book tab is selected: either a list of
 * location cards, or the empty state that fits the situation.
 *
 * Three different empties, because they mean different things. An empty
 * Favourites or Branches tab is a feature the customer hasn't used yet, so it
 * explains the feature and offers the action. An empty filter result is just a
 * search that missed, so it says so and nothing more — offering "add a
 * location" there would be answering a question nobody asked.
 */
export function LocationsAddressBookTabContent(
  props: LocationsAddressBookTabContentProps,
) {
  const {
    filter,
    showAddLocationDialog,
    filteredLocations,
    showModifyLocationDialog,
    showChangeContactDialog,
    onToggleFavourite,
    onToggleBranch,
    onDelete,
    onToggleDefaultPickupLocation,
    onToggleDefaultDeliveryLocation,
    defaultPickupLocationId,
    defaultDeliveryLocationId,
    selectedTab,
    onViewShipmentsToThisLocation,
    onViewShipmentsFromThisLocation,
    loadingLocations,
  } = props;

  // Enough of the viewport that an empty tab reads as the whole tab rather
  // than a message stranded under the strip. 300px is the chrome above and
  // below it: nav, page header, the tab strip itself, and the footer.
  const EMPTY_TAB_MIN_HEIGHT = "calc(100vh - 300px)";

  const isEmpty = filter === "" && filteredLocations.length === 0;

  if (isEmpty && selectedTab === LocationsAddressBookTabs.FavouriteLocations) {
    return (
      <Center maxWidth={768} minHeight={EMPTY_TAB_MIN_HEIGHT}>
        <Stack align="center" gap={8}>
          <Stack align="center" gap={1}>
            <Heading level={1}>
              You don&apos;t have any favourite locations yet
            </Heading>
            <T size="base" color="secondary" align="center">
              Favourite locations to book faster new time. You can either select
              a location from &quot;All Locations&quot; and mark as a favourite,
              or add a new location here
            </T>
          </Stack>
          <LocationsAddLocationButton onClick={showAddLocationDialog} />
        </Stack>
      </Center>
    );
  }

  if (isEmpty && selectedTab === LocationsAddressBookTabs.BranchLocations) {
    return (
      <Center maxWidth={768} minHeight={EMPTY_TAB_MIN_HEIGHT}>
        <Stack align="center" gap={8}>
          <Stack align="center" gap={1}>
            <Heading level={1}>You don&apos;t have any branches yet</Heading>
            <T size="base" color="secondary" align="center">
              Branches allow you to more easily manage a larger organization.
              You can either select a location from &quot;All Locations&quot;
              and mark as a favourite, or add a new location here
            </T>
          </Stack>
          <LocationsAddLocationButton onClick={showAddLocationDialog} />
        </Stack>
      </Center>
    );
  }

  if (filter !== "" && filteredLocations.length === 0) {
    return (
      <Center maxWidth={768} minHeight={EMPTY_TAB_MIN_HEIGHT}>
        <Heading level={3} size="lg" weight="medium">
          It looks like nothing matches this filter
        </Heading>
      </Center>
    );
  }

  return (
    <Stack gap={8}>
      {filteredLocations.map((item) => {
        const location = item.location;
        const defaultContact = item.contacts.find(
          (c) => c.savedContactId === item.defaultSavedContactId,
        );

        if (defaultContact === undefined) {
          throw new Error("Missing contact");
        }

        return (
          <LocationsBox
            key={item.savedLocationId}
            rowTestId={item.savedLocationId}
            location={location}
            contact={defaultContact.contact}
            contactCount={item.contacts.length}
            onModify={function () {
              showModifyLocationDialog(item);
            }}
            onChangeContact={() => {
              showChangeContactDialog(
                item.savedLocationId,
                defaultContact,
                item.contacts,
                item.defaultSavedContactId,
              );
            }}
            isFavourite={item.isFavourite}
            isBranch={item.isBranch}
            onToggleFavourite={function () {
              onToggleFavourite(item.savedLocationId);
            }}
            onToggleBranch={function () {
              onToggleBranch(item.savedLocationId);
            }}
            showMenu={true}
            onDelete={function () {
              onDelete(item.savedLocationId, item);
            }}
            context={LocationContext.Any}
            isDefaultPickupLocation={
              defaultPickupLocationId === item.savedLocationId
            }
            isDefaultDeliveryLocation={
              defaultDeliveryLocationId === item.savedLocationId
            }
            onToggleDefaultPickupLocation={function () {
              onToggleDefaultPickupLocation(item.savedLocationId);
            }}
            onToggleDefaultDeliveryLocation={function () {
              onToggleDefaultDeliveryLocation(item.savedLocationId);
            }}
            onViewShipmentsToThisLocation={function () {
              onViewShipmentsToThisLocation(item);
            }}
            onViewShipmentsFromThisLocation={function () {
              onViewShipmentsFromThisLocation(item);
            }}
            isLoading={loadingLocations[item.savedLocationId]}
            locationHasDock={item.locationHasDock}
            locationHasForklift={item.locationHasForklift}
          />
        );
      })}
    </Stack>
  );
}
